import React, { useEffect, useRef } from 'react';

/* ══════════════════════════════════════════════════════════════════════════
 * Cloudflare Turnstile widget (invisible/managed mode).
 *
 * Loads the Turnstile script once (process-wide), renders the widget with
 * explicit rendering, and reports the verification token via `onToken`
 * (empty string on error/expiry so the caller can invalidate a stale token).
 * The token is sent to the backend as `captchaToken` and verified server-side.
 *
 * Fails open: if the script can't load we simply never produce a token — the
 * backend's honeypot + rate-limit still apply, and captcha is only *enforced*
 * server-side when a secret is configured.
 * ══════════════════════════════════════════════════════════════════════════ */

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let scriptPromise = null;

function loadTurnstileScript() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.turnstile);
    s.onerror = () => reject(new Error('Turnstile script failed to load'));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

const Turnstile = ({ siteKey, onToken, className }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    if (!siteKey) return undefined;
    let cancelled = false;

    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelled || !turnstile || !containerRef.current) return;
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => onTokenRef.current && onTokenRef.current(token),
          'error-callback': () => onTokenRef.current && onTokenRef.current(''),
          'expired-callback': () => onTokenRef.current && onTokenRef.current(''),
        });
      })
      .catch(() => { /* fail open */ });

    return () => {
      cancelled = true;
      if (widgetIdRef.current != null && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch { /* noop */ }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey]);

  return <div ref={containerRef} className={className} />;
};

export default Turnstile;
